/**
 * Metric tile.
 *
 * A metric is never a bare number: it always shows its data class, and when a
 * value is unavailable it says so and explains why rather than rendering a
 * zero (FR-106).
 */

import type { FeatureValue } from '../lib/types'
import { featureValue, provenanceLine } from '../lib/format'
import { DataClassChip } from './Chips'

export function Metric({
  label,
  value,
  note,
  tone,
  title,
}: {
  label: string
  value: React.ReactNode
  note?: React.ReactNode
  tone?: 'ok' | 'warn' | 'danger'
  title?: string
}) {
  return (
    <div className="metric" title={title}>
      <div className="label">{label}</div>
      <div className="value" style={tone ? { color: `var(--${tone})` } : undefined}>
        {value}
      </div>
      {note ? <div className="note">{note}</div> : null}
    </div>
  )
}

export function FeatureMetric({
  label,
  feature,
  note,
}: {
  label: string
  feature: FeatureValue | null | undefined
  note?: string
}) {
  const unavailable = !feature || feature.data_class === 'unavailable' || feature.value === null
  return (
    <div className="metric" title={provenanceLine(feature)}>
      <div className="label">{label}</div>
      <div className={`value${unavailable ? ' unavailable' : ''}`}>
        {unavailable ? 'Unavailable' : featureValue(feature)}
      </div>
      <div className="row tight">
        <DataClassChip value={feature?.data_class ?? 'unavailable'} />
      </div>
      {unavailable && feature?.unavailable_reason ? (
        <div className="note">{feature.unavailable_reason}</div>
      ) : note ? (
        <div className="note">{note}</div>
      ) : null}
    </div>
  )
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack tight" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ width: `${100 - i * 12}%` }} />
      ))}
    </div>
  )
}
